'use client';

import { useState, useCallback } from 'react';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { EDGE_FUNCTIONS } from '@/lib/edgeFunctions';

const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? '';

function SubscribeFormInner() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');
    setErrorMsg('');

    // Get reCAPTCHA v3 token
    if (!executeRecaptcha) {
      setStatus('error');
      setErrorMsg('reCAPTCHA not ready. Please try again.');
      return;
    }

    let recaptchaToken: string;
    try {
      recaptchaToken = await executeRecaptcha('subscribe');
    } catch {
      setStatus('error');
      setErrorMsg('reCAPTCHA verification failed. Please try again.');
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();

    // Save to Supabase
    const supabase = createClient();
    const { error: dbError } = await supabase
      .from('email_subscribers')
      .insert({ email: normalizedEmail });

    if (dbError && dbError.code !== '23505') {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
      return;
    }

    // Sync to MailerLite with reCAPTCHA token
    try {
      const res = await fetch(EDGE_FUNCTIONS.mailerliteSubscribe, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail, recaptchaToken }),
      });
      if (!res.ok) {
        const data = await res.json();
        if (data?.error) {
          setStatus('error');
          setErrorMsg(data.error);
          return;
        }
      }
    } catch {
      // MailerLite sync failure is non-blocking
    }

    setStatus('success');
  }, [email, executeRecaptcha]);

  if (status === 'success') {
    return (
      <div className="mb-8 p-5 rounded-xl border border-green-200 bg-green-50 flex items-center gap-3">
        <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-green-800">You&apos;re on the list!</p>
          <p className="text-xs text-green-600 mt-0.5">We&apos;ll notify you when new picks are posted.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8 p-5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card-elevated))]">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg bg-[hsl(var(--primary)/0.1)] border border-[hsl(var(--primary)/0.2)] flex items-center justify-center flex-shrink-0">
          <Mail size={15} className="text-[hsl(var(--primary))]" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[hsl(var(--foreground))]">Get Pick Notifications</p>
          <p className="text-xs text-[hsl(var(--foreground-muted))] mt-0.5">
            Subscribe to be notified when today&apos;s picks are posted.
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          required
          className="flex-1 px-3 py-2 text-sm rounded-lg border border-[hsl(var(--border))] bg-white text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--foreground-muted))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)]"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary)/0.9)] disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
        >
          {status === 'loading' ? (
            <Loader2 size={14} className="animate-spin" />
          ) : null}
          Subscribe
        </button>
      </form>
      {status === 'error' && (
        <p className="mt-2 text-xs text-red-600">{errorMsg}</p>
      )}
    </div>
  );
}

export default function EmailSubscribeForm() {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={SITE_KEY}>
      <SubscribeFormInner />
    </GoogleReCaptchaProvider>
  );
}
